/**
 * Environment Configuration Service
 * Centralized environment management for AlphaCore
 *
 * This service provides a type-safe way to access environment-specific
 * configuration across the application. It automatically detects the
 * current environment and provides appropriate configuration values.
 *
 * Supported Environments:
 * - local: Full-stack development (localhost:5004)
 * - development: Dev server (dev.alphahealthai.com)
 * - qa: QA/Staging server (app.alphahealthai.com — API: 18.225.79.169)
 * - production: Production (TBD)
 */

export type EnvironmentType = "local" | "development" | "qa" | "production";

export interface AppEnvironment {
  name: EnvironmentType;
  apiBaseUrl: string;
  apiVersion: string;
  appName: string;
  appVersion: string;
  isDevelopment: boolean;
  isQA: boolean;
  isProduction: boolean;
  isLocal: boolean;
  showDevFeatures: boolean;
  enableAnalytics: boolean;
  enableWebSocket: boolean;
  enableRealTime: boolean;
  stripePublishableKey: string;
  enableStripePayments: boolean;
  maxFileSize: number;
  allowedFileTypes: string[];
  sentryDsn: string;
}

class EnvironmentService {
  private env: AppEnvironment;

  constructor() {
    this.env = this.loadEnvironment();
    this.validateEnvironment();
    this.logEnvironmentInfo();
  }

  private loadEnvironment(): AppEnvironment {
    const envName = (import.meta.env.VITE_ENVIRONMENT ||
      "development") as EnvironmentType;

    return {
      name: envName,
      apiBaseUrl: import.meta.env.VITE_API_BASE_URL || "",
      apiVersion: import.meta.env.VITE_API_VERSION || "v1",
      appName: import.meta.env.VITE_APP_NAME || "AlphaCore Case Management",
      appVersion: import.meta.env.VITE_APP_VERSION || "1.0.0",
      isDevelopment: envName === "development",
      isQA: envName === "qa",
      isProduction: envName === "production",
      isLocal: envName === "local",
      showDevFeatures: import.meta.env.VITE_SHOW_DEV_FEATURES === "true",
      enableAnalytics: import.meta.env.VITE_ENABLE_ANALYTICS === "true",
      enableWebSocket: import.meta.env.VITE_ENABLE_WEBSOCKET === "true",
      enableRealTime: import.meta.env.VITE_ENABLE_REAL_TIME === "true",
      stripePublishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "",
      enableStripePayments:
        import.meta.env.VITE_ENABLE_STRIPE_PAYMENTS === "true",
      maxFileSize: parseInt(import.meta.env.VITE_MAX_FILE_SIZE || "209715200"),
      allowedFileTypes: (import.meta.env.VITE_ALLOWED_FILE_TYPES || "")
        .split(",")
        .filter(Boolean),
      sentryDsn: import.meta.env.VITE_SENTRY_DSN || "",
    };
  }

  private validateEnvironment(): void {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Critical validations
    if (!this.env.apiBaseUrl) {
      errors.push("VITE_API_BASE_URL is not configured");
    }

    if (this.env.isProduction && !this.env.apiBaseUrl.startsWith("https")) {
      errors.push("Production environment must use HTTPS");
    }

    if (this.env.enableStripePayments && !this.env.stripePublishableKey) {
      errors.push("Stripe payments enabled but publishable key not configured");
    }

    // Check for test keys in production - but only when Stripe payments are enabled
    const hostname =
      typeof window !== "undefined" ? window.location.hostname : "";
    const isDevDomain =
      hostname.includes("dev.") ||
      hostname.includes("staging.") ||
      hostname.includes("localhost");

    if (
      this.env.isProduction &&
      this.env.enableStripePayments &&
      this.env.stripePublishableKey?.includes("test") &&
      !isDevDomain
    ) {
      errors.push(
        "Production environment is using TEST Stripe keys - must use LIVE keys",
      );
    } else if (
      this.env.isProduction &&
      this.env.enableStripePayments &&
      this.env.stripePublishableKey?.includes("test") &&
      isDevDomain
    ) {
      warnings.push(
        "Dev/staging domain using TEST Stripe keys with production config - this is expected for testing",
      );
    }

    // Warning validations
    if (this.env.isProduction && this.env.showDevFeatures) {
      warnings.push("Development features should be disabled in production");
    }

    if (
      !this.env.isProduction &&
      this.env.stripePublishableKey.includes("live")
    ) {
      warnings.push("Non-production environment is using LIVE Stripe keys");
    }

    // Log errors
    if (errors.length > 0) {
      console.error("❌ Environment configuration errors:", errors);
      throw new Error(
        `Environment configuration invalid: ${errors.join(", ")}`,
      );
    }

    // Log warnings
    if (warnings.length > 0) {
      console.warn("⚠️ Environment configuration warnings:", warnings);
    }
  }

  private logEnvironmentInfo(): void {
    // Environment logging disabled for production builds
  }

  // Getters
  get environment(): EnvironmentType {
    return this.env.name;
  }

  get apiBaseUrl(): string {
    return this.env.apiBaseUrl;
  }

  get apiVersion(): string {
    return this.env.apiVersion;
  }

  get appName(): string {
    return this.env.appName;
  }

  get appVersion(): string {
    return this.env.appVersion;
  }

  get isDevelopment(): boolean {
    return this.env.isDevelopment;
  }

  get isQA(): boolean {
    return this.env.isQA;
  }

  get isProduction(): boolean {
    return this.env.isProduction;
  }

  get isLocal(): boolean {
    return this.env.isLocal;
  }

  get showDevFeatures(): boolean {
    return this.env.showDevFeatures;
  }

  get enableAnalytics(): boolean {
    return this.env.enableAnalytics;
  }

  get stripePublishableKey(): string {
    return this.env.stripePublishableKey;
  }

  get enableStripePayments(): boolean {
    return this.env.enableStripePayments;
  }

  get maxFileSize(): number {
    return this.env.maxFileSize;
  }

  get allowedFileTypes(): string[] {
    return this.env.allowedFileTypes;
  }

  get sentryDsn(): string {
    return this.env.sentryDsn;
  }

  get config(): Readonly<AppEnvironment> {
    return Object.freeze({ ...this.env });
  }

  // Helper methods
  /**
   * Build a full API URL from a path
   * @param path - The API path (e.g., '/api/users' or 'api/users')
   * @returns Full API URL
   */
  buildApiUrl(path: string): string {
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `${this.env.apiBaseUrl}${cleanPath}`;
  }

  /**
   * Build a WebSocket URL from a path
   * @param path - The WebSocket path (default: '/ws')
   * @returns Full WebSocket URL
   */
  buildWebSocketUrl(path: string = "/ws"): string {
    const cleanPath = path.startsWith("/") ? path : `/${path}`;

    // On local dev the Vite dev server proxies /ws → backend, so use a
    // same-origin URL to avoid the CORS/origin rejection that occurs when
    // the browser tries to open a WS connection directly to a remote host.
    if (this.env.isLocal) {
      const protocol = window.location.protocol === "https:" ? "wss" : "ws";
      return `${protocol}://${window.location.host}${cleanPath}`;
    }

    const protocol = this.env.apiBaseUrl.startsWith("https") ? "wss" : "ws";
    const cleanUrl = this.env.apiBaseUrl
      .replace(/^https?:\/\//, "")
      .replace(/\/api\/?$/, "");
    return `${protocol}://${cleanUrl}${cleanPath}`;
  }

  /**
   * Check if a feature is enabled
   * @param feature - Feature name
   * @returns Whether the feature is enabled
   */
  isFeatureEnabled(feature: string): boolean {
    switch (feature.toLowerCase()) {
      case "websocket":
        return this.env.enableWebSocket;
      case "realtime":
        return this.env.enableRealTime;
      case "analytics":
        return this.env.enableAnalytics;
      case "stripe":
        return this.env.enableStripePayments;
      case "devfeatures":
        return this.env.showDevFeatures;
      case "sentry":
        return !!this.env.sentryDsn;
      default:
        return false;
    }
  }

  /**
   * Get human-readable environment label
   */
  getEnvironmentLabel(): string {
    switch (this.env.name) {
      case "local":
        return "Local Development";
      case "development":
        return "Development";
      case "qa":
        return "QA/Staging";
      case "production":
        return "Production";
      default:
        return this.env.name;
    }
  }

  /**
   * Get environment color for UI elements
   */
  getEnvironmentColor(): string {
    switch (this.env.name) {
      case "local":
        return "#2196F3"; // Blue
      case "development":
        return "#FF9800"; // Orange
      case "qa":
        return "#9C27B0"; // Purple
      case "production":
        return "#4CAF50"; // Green
      default:
        return "#757575"; // Grey
    }
  }
}

// Export singleton instance
export const environmentService = new EnvironmentService();

// Export convenience functions
export const getEnvironment = () => environmentService.environment;
export const getApiBaseUrl = () => environmentService.apiBaseUrl;
export const isProduction = () => environmentService.isProduction;
export const isDevelopment = () => environmentService.isDevelopment;
export const isQA = () => environmentService.isQA;
export const isLocal = () => environmentService.isLocal;
export const showDevFeatures = () => environmentService.showDevFeatures;

export default environmentService;
